// src/components/atomic-crm/activities/ActivityList.tsx
import { useMemo } from "react";
import { useLocaleState, useTranslate } from "ra-core";
import { AutocompleteInput } from "@/components/ds/admin/autocomplete-input";
import { CreateButton } from "@/components/ds/admin/create-button";
import { ExportButton } from "@/components/ds/admin/export-button";
import { FilterButton } from "@/components/ds/admin/filter-form";
import { List } from "@/components/ds/admin/list";
import { ReferenceInput } from "@/components/ds/admin/reference-input";
import { SearchInput } from "@/components/ds/admin/search-input";
import { SelectInput } from "@/components/ds/admin/select-input";
import { TopToolbar } from "../layout/TopToolbar";
import { TaskCardList } from "../tasks/TaskCardList";
import { MyTasksInput } from "../tasks/MyTasksInput";
import { ActiveFilterBar } from "../tasks/ActiveFilterBar";
import { translateChoice } from "@/i18n/utils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ActivityComposer } from "./ActivityComposer";
import { ActivityCreateButton } from "./ActivityCreateButton"; // 👈 Import this

const PAGE_SIZE = 25;

export const ActivityList = () => {
  const { taskStatuses, taskPriorities } = useConfigurationContext();
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const translatedTaskStatuses = useMemo(
    () =>
      taskStatuses.map((s) => ({
        ...s,
        name: translateChoice(translate, "crm.task.status", s.id, s.name),
      })),
    [taskStatuses, translate, locale],
  );

  const translatedTaskPriorities = useMemo(
    () =>
      taskPriorities.map((p) => ({
        ...p,
        name: translateChoice(translate, "crm.task.priority", p.id, p.name),
      })),
    [taskPriorities, translate, locale],
  );

  const activityFilters = [
    // <SearchInput source="q" alwaysOn key="q" />,
    <SelectInput
      key="type"
      source="type"
      choices={[
        { id: "note", name: "Note" },
        { id: "task", name: "Task" },
      ]}
      alwaysOn
      label="Type"
    />,
    <SelectInput
      key="status"
      source="status"
      choices={translatedTaskStatuses}
      label={translate("crm.task.field.status")}
    />,
    <ReferenceInput key="contact" source="contact_id" reference="contacts">
      <AutocompleteInput
        label={false}
        placeholder={translate("crm.filter.contact")}
      />
    </ReferenceInput>,
    <ReferenceInput key="company" source="company_id" reference="companies">
      <AutocompleteInput
        label={false}
        placeholder={translate("crm.filter.company")}
        optionText="name"
      />
    </ReferenceInput>,
    <ReferenceInput key="deal" source="deal_id" reference="deals">
      <AutocompleteInput
        label={false}
        placeholder={translate("crm.filter.deal")}
        optionText="name"
      />
    </ReferenceInput>,
    <SelectInput
      key="priority"
      source="priority"
      choices={translatedTaskPriorities}
      label={translate("crm.task.field.priority")}
    />,
    <MyTasksInput
      key="assigned"
      label={translate("crm.filter.my_tasks")}
      alwaysOn
    />,
  ];

  return (
    <div className="space-y-4">
      <List
        resource="crm_activities"
        perPage={PAGE_SIZE}
        sort={{ field: "date", order: "DESC" }}
        filters={activityFilters}
        actions={<ActivityActions />}
        title={translate("crm.nav.activities", { _: "Activities" })}
      >
        <ActiveFilterBar />
        <TaskCardList />
      </List>
    </div>
  );
};

const ActivityActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar className="items-center">
      <FilterButton />

      {/* 👈 Swapped CreateButton for ActivityCreateButton */}
      <ActivityCreateButton />
    </TopToolbar>
  );
};
